<template>
  <div>
    <div class="BasicInfo Content">
      <event-stage-header />
      <main class="SectionContainer u-flex">
        <section :class="[{'is-loading': ui.isLoading} ,'SectionContainer-left']">
          <div>
            <rmp-input
              v-model.trim="fields.name"
              :required="true"
              :error="validateStatus($v.fields.name, 'Name')"
              :manual-validate="validateAll"
              label="Event Name"
            />
            <hr>
            <rmp-date-picker
              v-if="isEventCreation"
              v-model="fields.start_date"
              :required="true"
              :error="validateStatus($v.fields.start_date, 'Date')"
              :manual-validate="validateAll"
              :disable-dates="disableDatesForEventDate"
              placeholder="DD/MM/YY"
              label="Event Date"
            />
            <div class="Form-group StopCollectingRecordsAt">
              <rmp-date-picker
                v-if="isEventCreation"
                v-model="fields.stop_collecting_registrations_at_date"
                :required="false"
                :manual-validate="validateAll"
                :disable-dates="disableDatesForStopCollectingRegistrationsDate()"
                placeholder="DD/MM/YY"
                label="Stop Collecting Registrations at"
                :clearable="true"
                style="width: 40%;"
                @cleared="disableDatesForStopCollectingRegistrationsDateWhenCleared()"
              />
              <rmp-select
                v-model="fields.stop_collecting_registrations_at_hour"
                :items="getNumberArray(1,23,1)"
                style="width: 115px; margin-right: 10px;"
                label="Hour"
              />
              <rmp-select
                v-model="fields.stop_collecting_registrations_at_minutes"
                :items="getNumberArray(0,55,5)"
                style="width: 115px"
                label="Minutes"
              />
              <div class="StopCollectingRecordsAt-TimeZone">
                Timezone: Europe/London
              </div>
            </div>
            <rmp-autocomplete-select
              v-if="dataInteractionTypes"
              v-model="fields.interaction_type"
              :required="true"
              index="formatted_text"
              :taggable="true"
              placeholder="Start typing an interaction type"
              :error="
                validateStatus($v.fields.interaction_type, 'Interaction Type')
              "
              :manual-validate="validateAll"
              label="Interaction Type"
              :items="dataInteractionTypes"
              option-key="formatted_text"
            />
            <p
              v-if="
                fields.interaction_type !== null &&
                  Object.keys(fields.interaction_type).length === 1
              "
              class="Notice Notice--warning"
            >
              A new interaction type
              <strong>{{ fields.interaction_type.formatted_text }}</strong> will
              be created! Double check spelling....
            </p>
            <rmp-checkbox
              v-if="isEventCreation"
              v-model="fields.is_visible"
              :value="true"
              label="Set Visible"
            />
          </div>
          <div class="Form-field Form-input MaxNumberOfRegistrations">
            <label>Max Number of Registrations</label>
            <div>
              <input
                v-model="fields.max_number_of_responses"
                type="number"
                min="1"
                name="max_number_of_responses"
              >
            </div>
          </div>
        </section>

        <section class="SectionContainer-right">
          <header-creation-steps
            selected="Basic Info"
            :event="event"
          />
          <div
            v-if="isEventCreation"
            class="EventInstructions"
          >
            <h1>Event creation instructions</h1>
            <h3> This is the key information required to set up your form.</h3>
            <ul class="InstructionList">
              <li>
                <strong>Event name</strong>: Name of the event. This can be a heading for the online form and will be
                used for reporting. Please make this as unique as possible e.g. Bath careers fair 2022
              </li>
              <li>
                <strong>Event date</strong>: The date the event is taking place. The form will accept registrations
                until this date.
              </li>
              <li>
                <strong>Stop Collecting Registrations at</strong>: The form will stop collecting registrations
                after the defined date/time. This supersedes the event date. The timezone is Europe/London.
              </li>
              <li>
                <strong>Interaction Type</strong>: The most important step to ensure the data syncs through to the
                Talent Pool. Assign the event an interaction type e.g. Careers Fair 22/23, High Fliers Competition, RYI
                2022.
              </li>
              <li>
                <strong>Set Visible</strong>: By setting a form as visible you will be able to generate a URL and/or
                embed code to share. When not visible, it means that the students won't be able to view the form even if
                the event date has not expired.
              </li>
              <li>
                <strong>Set Max Number of Registrations</strong> Use this to set a limit on the number of registrations.
                Please take into consideration that is possible to have between 1 to 5 registrations beyond the limit.
              </li>
            </ul>
          </div>
        </section>
      </main>
      <footer-creation-buttons v-if="isEventCreation">
        <template
          v-if="false"
          #left-aligned-buttons
        />
        <template
          v-if="!ui.isLoading"
          #right-aligned-buttons
        >
          <button
            :class="[{'is-loading': ui.submitLoading }, 'Button Button--green']"
            @click="submit"
          >
            Save & Continue
          </button>
        </template>
      </footer-creation-buttons>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import RmpInput from '@rmpenterprise/form-input';
import RmpCheckbox from '@rmpenterprise/form-checkbox';
import RmpDatePicker from '@rmpenterprise/form-date-picker';
import RmpAutocompleteSelect from '@rmpenterprise/form-autocomplete-select';
import RmpSelect from '@rmpenterprise/form-select';
import HeaderCreationSteps from '../common/header-creation-steps.vue';
import FooterCreationButtons from '../common/footer-creation-buttons.vue';
import validationMixin from '../../../mixins/validation-mixin';
import dateTransformationsMixin from '../../../mixins/date-transformations';
import EventStageHeader from '../common/event-stage-header';

export default {
  components: {
    RmpSelect,
    HeaderCreationSteps,
    FooterCreationButtons,
    EventStageHeader,
    RmpInput,
    RmpDatePicker,
    RmpAutocompleteSelect,
    RmpCheckbox,
  },
  mixins: [validationMixin, dateTransformationsMixin],
  data() {
    return {
      pageTitle: 'Basic Info',
      validateAll: false,
      ui: {
        isLoading: false,
        submitLoading: false,
      },
      fields: {
        name: null,
        start_date: null,
        stop_collecting_registrations_at_date: null,
        stop_collecting_registrations_at_hour: null,
        stop_collecting_registrations_at_minutes: null,
        interaction_type: null,
        is_visible: true,
      },
    };
  },
  computed: {
    ...mapState('events', {
      event: (state) => state.event,
      dataInteractionTypes: (state) => state.lists.dataInteractionTypes,
    }),
    disableDatesForEventDate() {
      const date = new Date();
      const yesterday = new Date(date.setDate(date.getDate() - 1));
      return {
        to: yesterday,
      };
    },
    isEventCreation() {
      return this.$route.path.indexOf('create-event') > -1;
    },
    isDynamicFormCreation() {
      return this.$route.path.indexOf('dynamic') > -1;
    },
  },
  watch: {
    'fields.stop_collecting_registrations_at_date': {
      handler() {
        if (this.fields.stop_collecting_registrations_at_date !== null) {
          return;
        }

        this.fields.stop_collecting_registrations_at_hour = null;
        this.fields.stop_collecting_registrations_at_minutes = null;
      },
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    if (this.$route.params.id !== undefined) {
      this.ui.isLoading = true;
      this.getEvent(this.$route.params.id)
        .then(() => {
          if (this.event.is_locked) {
            this.$router.push({ name: 'event-records', params: { eventId: this.event.id } });
          } else {
            this.populateFields();
          }
        })
        .catch(({ data }) => {
          this.ui.isLoading = false;
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'warning',
          });
        });
    }

    this.getInteractionTypes();
  },
  methods: {
    ...mapActions('events', ['createEvent', 'getEvent', 'updateEvent', 'getInteractionTypes']),
    /**
     * After the clear event, if the date picker has disabled dates then the "from" date actually goes back a day.
     * This method is used by the clear event and resets the disabled dates in order to fix the odd behaviour.
     * @returns {{from: Date}}
     */
    disableDatesForStopCollectingRegistrationsDateWhenCleared() {
      const date = this.fields.start_date;
      return {
        from: new Date(new Date(date.setDate(date.getDate() + 1))),
      };
    },
    disableDatesForStopCollectingRegistrationsDate() {
      return {
        from: new Date(this.fields.start_date),
      };
    },
    getNumberArray(start, end, increment = 1) {
      // Helper funciton can probably be added to a utlitiies file or the store.
      // Taken from SMS code base to genenerate years
      const len = Math.floor((end - start) / increment) + 1;
      // Get numbers and add 0 to any number that's less than 2
      return Array(len).fill().map((_, index) => String(start + (index * increment)).padStart(2, 0));
    },
    populateFields() {
      this.fields = { ...this.event };
      this.fields.start_date = this.transformDateFromStringToDateObject(this.fields.start_date);
      this.fields.stop_collecting_registrations_at_date = this.fields.stop_collecting_registrations_at_date !== null
        ? this.transformDateFromStringToDateObject(this.fields.stop_collecting_registrations_at_date)
        : null;

      this.ui.isLoading = false;
    },
    submit() {
      this.validateAll = true;
      if (this.$v.fields.$invalid) {
        return;
      }
      this.ui.submitLoading = true;
      this.validateAll = false;
      const payload = { ...this.fields };

      if (this.isDynamicFormCreation) {
        payload.type = 'dynamic';
      }

      payload.start_date = this.transformDateObjectIntoDateAsString(payload.start_date);
      payload.stop_collecting_registrations_at_date = payload.stop_collecting_registrations_at_date !== null
        ? this.transformDateObjectIntoDateAsString(payload.stop_collecting_registrations_at_date)
        : null;

      if (this.$route.params.id === undefined) {
        this.createEvent({ payload })
          .then(() => {
            this.ui.submitLoading = false;
            if (this.event.type === 'dynamic') {
              this.$router.push({ name: 'form-builder-dynamic-form', params: { id: this.event.id } });
            } else {
              this.$router.push({ name: 'form-builder', params: { id: this.event.id } });
            }
          })
          .catch(({ data }) => {
            let { message } = data.data;
            if (data.meta && data.meta[0]) {
              message = data.meta[0].message;
            }
            this.ui.submitLoading = false;
            this.$bus.$emit('displayMsg', {
              text: message,
              type: 'error',
            });
          });
      } else {
        payload.id = this.event.id;
        this.updateEvent(payload)
          .then(() => {
            this.ui.submitLoading = false;
            if (this.event.type === 'dynamic') {
              this.$router.push({ name: 'form-builder-dynamic-form', params: { id: this.event.id } });
            } else {
              this.$router.push({ name: 'form-builder', params: { id: this.event.id } });
            }
          })
          .catch(({ data }) => {
            let { message } = data.data;
            if (data.meta && data.meta[0]) {
              message = data.meta[0].message;
            }
            this.ui.submitLoading = false;
            this.$bus.$emit('displayMsg', {
              text: message,
              type: 'error',
              time: 10000,
            });
          });
      }
    },
  },
  validations() {
    const rules = {
      fields: {
        name: {
          required,
        },
        start_date: {
          required,
        },
        interaction_type: {
          required,
        },
        is_visible: {
          required,
        },
      },
    };

    return rules;
  },
};
</script>

<style lang="scss" scoped>

.StopCollectingRecordsAt {
  .StopCollectingRecordsAt-TimeZone {
    margin-top: 30px;
  }
}

.MaxNumberOfRegistrations{
  //width: 40%;
}

.SectionContainer .SectionContainer-left {
  padding: 34px 0 0 0;
}

.SectionContainer-right {
  position: relative;
}

.EventInstructions {
  h1 {
    margin-bottom: 10px;
  }

  .InstructionList {
    list-style: decimal;
    margin-left: 15px;

    li {
      padding: 10px;
    }
  }
}

.Notice--warning,
.VisibleHint {
  margin: -22px 30px 25px 30px;
}

.SaveExitButton {
  margin-right: 15px;
}
</style>
